import type { CatalogueSharingMode } from "../../types/catalogue.types";
import { studioCatalogueAuthHeaders } from "./catalogueSecurityApi";
import { buildPublicJsonHeaders } from "../../pages/catalog/ecomerce/publicCatalogueHelpers";

type ApiEnvelope<T> = {
  success?: boolean;
  message?: string;
  status?: number;
  data?: T;
};

const SESSION_HEADER = "X-HKP-Catalogue-Session";

const apiBase = (): string => (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

const resellerPath = (catalogueId: string, suffix = ""): string =>
  `${apiBase()}/catalogue-reseller/${encodeURIComponent(catalogueId)}${suffix}`;

async function readEnvelope<T>(res: Response): Promise<ApiEnvelope<T>> {
  const text = await res.text();
  if (!text) {
    return { success: res.ok, status: res.status };
  }
  try {
    const json = JSON.parse(text) as ApiEnvelope<T>;
    return { ...json, status: json.status ?? res.status };
  } catch {
    return { success: false, status: res.status, message: "Unexpected response from server." };
  }
}

function viewerHeaders(sessionToken?: string, json = false): HeadersInit {
  const headers: Record<string, string> = {};
  if (json) headers["Content-Type"] = "application/json";
  if (sessionToken) headers[SESSION_HEADER] = sessionToken;
  return headers;
}

const linkQuery = (linkToken: string, extra = ""): string =>
  `?link=${encodeURIComponent(linkToken)}${extra}`;

// ---- Reseller -----------------------------------------------------------------------------------

export interface ResellerClientRow {
  linkId: string;
  clientName: string;
  clientPhone?: string;
  /** Dial code stored on the client link (e.g. 91, 971). */
  clientCountryCode?: string | null;
  clientPhoneMasked?: string;
  createdAt?: string;
  expiresAt?: string;
  status: "ACTIVE" | "EXPIRED" | "REVOKED";
  /** OPEN: anyone with the link can browse. EXCLUSIVE: only the client's number. */
  accessMode?: "OPEN" | "EXCLUSIVE";
  views?: number;
  likes?: number;
  publicUrl?: string;
  markupPercent?: number | null;
  pieceCount?: number | null;
}

export interface ResellerSummary {
  forwardLimit: number;
  forwardsUsed: number;
  forwardsRemaining: number;
  brandMode?: string;
  resellerName?: string;
  linkExpiresAt?: string;
  clients: ResellerClientRow[];
}

export interface CreateClientLinkRequest {
  clientName: string;
  clientPhone: string;
  clientCountryCode: string;
  /** Empty = every piece in the collection. */
  productUniqueIds?: string[];
  markupPercent?: number;
  priceOverrides?: Record<string, number>;
  expiryDays?: number;
  /** EXCLUSIVE (default) or OPEN. */
  accessMode?: "OPEN" | "EXCLUSIVE";
}

export interface CreatedClientLink {
  id: string;
  clientName: string;
  /** Empty for an open link sent without a mobile. */
  clientPhone?: string;
  clientCountryCode?: string;
  publicUrl: string;
  expiresAt?: string;
  catalogueName?: string;
  resellerName?: string;
  forwardLimit: number;
  forwardsUsed: number;
  forwardsRemaining: number;
}

export async function fetchResellerSummary(
  catalogueId: string,
  linkToken: string,
  sessionToken?: string
): Promise<ApiEnvelope<ResellerSummary>> {
  const res = await fetch(resellerPath(catalogueId, `/me${linkQuery(linkToken)}`), {
    headers: viewerHeaders(sessionToken),
  });
  return readEnvelope<ResellerSummary>(res);
}

export async function createClientLink(
  catalogueId: string,
  linkToken: string,
  sessionToken: string | undefined,
  body: CreateClientLinkRequest
): Promise<ApiEnvelope<CreatedClientLink>> {
  const res = await fetch(resellerPath(catalogueId, `/client-links${linkQuery(linkToken)}`), {
    method: "POST",
    headers: viewerHeaders(sessionToken, true),
    body: JSON.stringify(body),
  });
  return readEnvelope<CreatedClientLink>(res);
}

export async function revokeClientLink(
  catalogueId: string,
  linkToken: string,
  sessionToken: string | undefined,
  clientLinkId: string
): Promise<ApiEnvelope<ResellerSummary>> {
  const res = await fetch(
    resellerPath(catalogueId, `/client-links/${encodeURIComponent(clientLinkId)}/revoke${linkQuery(linkToken)}`),
    { method: "PUT", headers: viewerHeaders(sessionToken) }
  );
  return readEnvelope<ResellerSummary>(res);
}

export async function requestMoreShares(
  catalogueId: string,
  linkToken: string,
  sessionToken?: string
): Promise<ApiEnvelope<{ sent: boolean }>> {
  const res = await fetch(resellerPath(catalogueId, `/request-more${linkQuery(linkToken)}`), {
    method: "POST",
    headers: viewerHeaders(sessionToken),
  });
  return readEnvelope<{ sent: boolean }>(res);
}

// ---- Client -------------------------------------------------------------------------------------

export async function fetchClientLikes(
  catalogueId: string,
  linkToken: string,
  sessionToken?: string
): Promise<string[]> {
  try {
    const res = await fetch(resellerPath(catalogueId, `/likes${linkQuery(linkToken)}`), {
      headers: viewerHeaders(sessionToken),
    });
    const env = await readEnvelope<{ productUniqueIds: string[] }>(res);
    return env.success && Array.isArray(env.data?.productUniqueIds) ? env.data!.productUniqueIds : [];
  } catch {
    return [];
  }
}

export async function setClientLike(
  catalogueId: string,
  linkToken: string,
  sessionToken: string | undefined,
  productUniqueId: string,
  liked: boolean
): Promise<boolean> {
  try {
    const res = await fetch(
      resellerPath(
        catalogueId,
        `/likes/${encodeURIComponent(productUniqueId)}${linkQuery(linkToken, `&liked=${liked}`)}`
      ),
      { method: "PUT", headers: viewerHeaders(sessionToken) }
    );
    const env = await readEnvelope<unknown>(res);
    return Boolean(env.success);
  } catch {
    return false;
  }
}

// ---- Interest ---------------------------------------------------------------------------------

/** The buyer opened a piece's making story. Best effort: failures never reach the buyer. */
export async function recordStoryOpen(
  catalogueId: string,
  linkToken: string,
  sessionToken: string | undefined,
  productUniqueId: string
): Promise<void> {
  if (!sessionToken || !linkToken || !productUniqueId) return;
  try {
    await fetch(`${apiBase()}/catalogue-story/${encodeURIComponent(catalogueId)}/opens${linkQuery(linkToken)}`, {
      method: "POST",
      headers: viewerHeaders(sessionToken, true),
      body: JSON.stringify({ productUniqueId }),
      keepalive: true,
    });
  } catch {
    /* analytics only */
  }
}

/** Short link (/w/<code>) to one piece on this share link; null when it could not be made. */
export async function createPieceLink(
  catalogueId: string,
  linkToken: string,
  sessionToken: string | undefined,
  productUniqueId: string
): Promise<string | null> {
  if (!sessionToken || !linkToken || !productUniqueId) return null;
  try {
    const res = await fetch(`${apiBase()}/catalogue-piece-link/${encodeURIComponent(catalogueId)}${linkQuery(linkToken)}`, {
      method: "POST",
      headers: viewerHeaders(sessionToken, true),
      body: JSON.stringify({ productUniqueId }),
    });
    const env = await readEnvelope<{ code?: string }>(res);
    return env.success && env.data?.code ? env.data.code : null;
  } catch {
    return null;
  }
}

/** Where a short piece link goes: the studio's own piece, or the share link and the id its viewer uses. */
export interface PieceLinkTarget {
  catalogueId: string;
  studio: boolean;
  productUniqueId?: string;
  link?: string;
  pieceId?: string;
}

export async function resolvePieceLink(code: string): Promise<ApiEnvelope<PieceLinkTarget>> {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
  const res = await fetch(`${apiBase()}/catalogue-piece-link/${encodeURIComponent(code)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return readEnvelope<PieceLinkTarget>(res);
}

export interface StoryOpenRow {
  productUniqueId: string;
  productName?: string;
  linkToken?: string;
  viewerName?: string | null;
  linkRole?: string;
  opens: number;
  firstOpenedAt?: string;
  lastOpenedAt?: string;
}

export async function fetchStoryOpens(catalogueId: string): Promise<ApiEnvelope<StoryOpenRow[]>> {
  const res = await fetch(`${apiBase()}/catalogue-story/${encodeURIComponent(catalogueId)}/opens`, {
    headers: studioCatalogueAuthHeaders(),
  });
  return readEnvelope<StoryOpenRow[]>(res);
}

// ---- OPEN mode ----------------------------------------------------------------------------------

/** A gated group link: what the viewer typed on the gate form. */
export interface OpenSessionViewer {
  viewerName: string;
  viewerPhone?: string;
  viewerCountryCode?: string;
  otp?: string;
}

export interface OpenSessionPayload {
  sessionToken: string;
  recipientName?: string;
  maskedPhone?: string;
  phoneVerified?: boolean;
  /** Set by the server when the viewer still has to enter the SMS code. */
  otpRequired?: boolean;
}

export async function openCatalogueSession(
  catalogueId: string,
  linkToken: string,
  viewer?: OpenSessionViewer
): Promise<ApiEnvelope<OpenSessionPayload>> {
  const body: Record<string, string> = { linkToken };
  if (viewer) {
    body.viewerName = viewer.viewerName;
    if (viewer.viewerPhone) body.viewerPhone = viewer.viewerPhone;
    if (viewer.viewerCountryCode) body.viewerCountryCode = viewer.viewerCountryCode;
    if (viewer.otp) body.otp = viewer.otp;
  }
  const res = await fetch(
    `${apiBase()}/catalogue-security/${encodeURIComponent(catalogueId)}/open-session`,
    {
      method: "POST",
      // The studio's own JWT (when signed in) marks the open as a preview, not a buyer.
      headers: buildPublicJsonHeaders({ includeStudioJwt: true }),
      body: JSON.stringify(body),
    }
  );
  return readEnvelope<OpenSessionPayload>(res);
}

// ---- Studio -------------------------------------------------------------------------------------

export interface StudioResellerNode {
  linkId: string;
  resellerName: string;
  resellerPhoneMasked: string;
  createdAt?: string;
  expiresAt?: string;
  disabled?: boolean;
  brandMode?: string;
  views?: number;
  forwardLimit: number;
  forwardsUsed: number;
  forwardsRemaining: number;
  clients: ResellerClientRow[];
}

export async function setCatalogueSharingMode(
  catalogueId: string,
  sharingMode: CatalogueSharingMode
): Promise<ApiEnvelope<{ sharingMode: CatalogueSharingMode }>> {
  const res = await fetch(resellerPath(catalogueId, "/sharing-mode"), {
    method: "PUT",
    headers: studioCatalogueAuthHeaders(),
    body: JSON.stringify({ sharingMode }),
  });
  return readEnvelope<{ sharingMode: CatalogueSharingMode }>(res);
}

export async function fetchResellerTree(catalogueId: string): Promise<ApiEnvelope<StudioResellerNode[]>> {
  const res = await fetch(resellerPath(catalogueId, "/tree"), {
    headers: studioCatalogueAuthHeaders(),
  });
  return readEnvelope<StudioResellerNode[]>(res);
}
